// src/components/AdminLayout.js
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Divider,
  Breadcrumbs,
  Link,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
  Badge,
  Menu,
  MenuItem,
  Button,
  Fade,
  Tooltip,
} from "@mui/material";

import DashboardIcon from "@mui/icons-material/Dashboard";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BusinessIcon from "@mui/icons-material/Business";
import EngineeringIcon from "@mui/icons-material/Engineering";
import NotificationsIcon from "@mui/icons-material/Notifications";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import MapIcon from "@mui/icons-material/Map";
import SettingsIcon from "@mui/icons-material/Settings";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SpeedIcon from "@mui/icons-material/Speed";

// Import contexts
import { useAuth } from "../contexts/AuthContext";
import { useNavigation } from "../contexts/NavigationContext";
import { db, functions } from "../firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

const drawerWidth = 260;

// Responsive drawer width
const drawerWidthMobile = 220;
const navItems = [
  { 
    id: "dashboard", 
    label: "Dashboard", 
    icon: <DashboardIcon />,
    description: "Overview of all jobs"
  },
  {
    id: "jobRequests",
    label: "Job Requests",
    icon: <NotificationsIcon />,
    description: "Review and approve new requests"
  },
  { 
    id: "createJob", 
    label: "Create Job", 
    icon: <AddCircleOutlineIcon />,
    description: "Create a new job",
    primary: true
  },
  { 
    id: "clients", 
    label: "Clients", 
    icon: <BusinessIcon />,
    description: "Manage clients"
  },
  { 
    id: "engineers", 
    label: "Engineers", 
    icon: <EngineeringIcon />,
    description: "Manage engineers"
  },
  { 
    id: "bulkPortal", 
    label: "Bulk Portal", 
    icon: <GroupAddIcon />,
    description: "Enable portal for multiple customers"
  },
  { 
    id: "invoices", 
    label: "Invoices", 
    icon: <ReceiptLongIcon />,
    description: "Generate invoices"
  },
  { 
    id: "designer", 
    label: "Job Designer", 
    icon: <DesignServicesIcon />,
    description: "Design job templates"
  },
  { 
    id: "liveMap", 
    label: "Live Map", 
    icon: <MapIcon />,
    description: "Track engineers"
  },
  { 
    id: "settings", 
    label: "Settings", 
    icon: <SettingsIcon />,
    description: "System settings"
  },
];

function AdminLayout({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [requestCount, setRequestCount] = useState(0);
  const [anchorElUser, setAnchorElUser] = useState(null);

  // Use contexts
  const { user, userProfile, logout } = useAuth();
  const { 
    adminPage, 
    navigateToAdminPage, 
    navigationHistory, 
  } = useNavigation();

  // Temporary function to run the data fix script
  const handleFixData = async () => {
    if (!window.confirm("This will run a script to fix customer data IDs. It should only be run once. Are you sure you want to proceed?")) {
      return;
    }
    try {
      alert("Running fix script... This may take a moment. Check your browser's developer console (F12) for progress.");
      const fixClientIds = httpsCallable(functions, 'fixClientIds');
      const result = await fixClientIds();
      console.log('Fix script result:', result.data);
      alert(`Fix script completed successfully! ${result.data.message}`);
    } catch (error) {
      console.error("Error running fix script:", error);
      alert(`An error occurred: ${error.message}. Check the console for details.`);
    }
  };

  // Listen for pending job requests
  useEffect(() => {
    const q = query(collection(db, "jobRequests"), where("status", "==", "pending"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setRequestCount(snapshot.size);
    });
    return () => unsubscribe();
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigate = (pageId) => {
    navigateToAdminPage(pageId);
    if (isMobile) {
      setMobileOpen(false);
    }
    setSearchQuery("");
  };

  const handleUserMenuOpen = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  // Filter nav items based on search
  const filteredNavItems = useMemo(() => {
    if (!searchQuery) return navItems;
    const query = searchQuery.toLowerCase();
    return navItems.filter(item => 
      item.label.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Render breadcrumbs
  const renderBreadcrumbs = () => {
    if (!navigationHistory || navigationHistory.length <= 1) {
      return null;
    }

    return (
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2 }}
      >
        {navigationHistory.map((item, index) => {
          const isLast = index === navigationHistory.length - 1;
          
          if (isLast) {
            return (
              <Typography
                key={index}
                color="text.primary"
                sx={{ fontWeight: 600 }}
              >
                {item.label}
              </Typography>
            );
          }

          return (
            <Link
              key={index}
              underline="hover"
              color="text.secondary"
              onClick={() => {
                if (item.page) {
                  navigateToAdminPage(item.page);
                }
              }}
              sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
            >
              {item.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    );
  };

  // Quick action button
  const QuickJobButton = () => (
    <Button
      variant="contained"
      size="small"
      startIcon={<AddCircleOutlineIcon />}
      onClick={() => handleNavigate('createJob')}
      sx={{
        mr: 2,
        display: { xs: 'none', sm: 'flex' },
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 4px 14px 0 rgba(102,126,234,0.4)',
        '&:hover': {
          background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)',
          boxShadow: '0 6px 20px 0 rgba(102,126,234,0.6)',
        },
      }}
    >
      Quick Job
    </Button>
  );

  // Drawer content
  const drawerContent = (
    <>
      <Toolbar
        sx={{
          px: { xs: 2, sm: 3 },
          minHeight: { xs: 56, sm: 72 },
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {/* Logo + App name */}
        <Avatar
          src="/humbertrack-logo.png"
          alt="HumberTrack"
          sx={{ 
            width: { xs: 32, sm: 40 }, 
            height: { xs: 32, sm: 40 }, 
            mr: 1,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            fontWeight: 700,
          }}
        >
          H
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            HumberTrack
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Admin Console
          </Typography>
        </Box>
        {isMobile && (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{ ml: 'auto' }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Toolbar>

      <Divider sx={{ borderColor: "rgba(148,163,184,0.2)" }} />

      {/* Search bar in sidebar */}
      <Box sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search pages..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(148,163,184,0.05)',
              '&:hover': {
                bgcolor: 'rgba(148,163,184,0.08)',
              },
            },
          }}
        />
      </Box>

      {/* NAV ITEMS */}
      <List sx={{ px: 1, flexGrow: 1, overflowY: 'auto' }}>
        {filteredNavItems.map((item) => {
          const isSelected = adminPage === item.id;
          
          return (
            <Tooltip
              key={item.id}
              title={item.description}
              placement="right"
              arrow
            >
              <ListItemButton
                selected={isSelected}
                onClick={() => handleNavigate(item.id)}
                sx={{
                  my: 0.5,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  "&.Mui-selected": {
                    bgcolor: "rgba(102, 126, 234, 0.15)",
                    borderLeft: '3px solid #667eea',
                    "&:hover": {
                      bgcolor: "rgba(102, 126, 234, 0.25)",
                    },
                    "&::before": {
                      content: '""',
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      height: '100%',
                      width: '4px',
                      background: 'linear-gradient(180deg, #667eea, #764ba2)',
                      borderRadius: '4px 0 0 4px',
                    },
                  },
                  "&:hover": {
                    bgcolor: "rgba(148,163,184,0.1)",
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: isSelected ? "primary.main" : "text.secondary",
                    minWidth: 40,
                  }}
                >
                  {item.id === 'jobRequests' && requestCount > 0 ? (
                    <Badge badgeContent={requestCount} color="error">
                      {item.icon}
                    </Badge>
                  ) : item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ 
                    fontSize: 14, 
                    fontWeight: isSelected ? 600 : 500,
                  }}
                />
                {item.primary && (
                  <SpeedIcon 
                    sx={{ 
                      fontSize: 16, 
                      color: 'warning.main',
                      opacity: 0.7 
                    }} 
                  />
                )}
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>

      {/* FOOTER / USER + LOGOUT */}
      <Box sx={{ p: 2, borderTop: "1px solid rgba(148,163,184,0.2)" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1.5,
            gap: 1.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "rgba(148,163,184,0.05)",
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: "rgba(148,163,184,0.1)",
            },
          }}
          onClick={handleUserMenuOpen}
        >
          <Avatar 
            sx={{ 
              width: 36, 
              height: 36,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              fontWeight: 700,
            }}
          >
            {user?.email?.[0]?.toUpperCase() || "A"}
          </Avatar>
          <Box sx={{ overflow: "hidden", flex: 1 }}>
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: 600, maxWidth: 140 }}
            >
              {userProfile?.displayName || user?.email?.split('@')[0] || "Admin"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Administrator
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* SIDEBAR - Desktop */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              borderRight: "1px solid rgba(148, 163, 184, 0.2)",
              bgcolor: "rgba(15,23,42,0.96)",
              backdropFilter: "blur(18px)",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* SIDEBAR - Mobile */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidthMobile,
              boxSizing: "border-box",
              bgcolor: "rgba(15,23,42,0.98)",
              backdropFilter: "blur(18px)",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* MAIN CONTENT */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: "100vh",
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        {/* Top bar (sticky) */}
        <AppBar
          position="fixed"
          sx={{
            ml: { sm: `${drawerWidth}px` },
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            bgcolor: "rgba(15,23,42,0.95)",
            backdropFilter: "blur(18px)",
            boxShadow: "0 10px 30px rgba(15,23,42,0.8)",
            borderBottom: '1px solid rgba(148,163,184,0.1)',
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            
            <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              {(() => {
                const active = navItems.find((n) => n.id === adminPage);
                return active ? active.label : "Dashboard";
              })()}
            </Typography>

            {/* Quick Job Button */}
            {!isMobile && <QuickJobButton />}

            {/* TEMPORARY FIX BUTTON */}
            <Button color="warning" variant="outlined" onClick={handleFixData} sx={{mr: 2}}>Fix Data</Button>

            {/* Help */}
            <Tooltip title="Help & Documentation">
              <IconButton color="inherit" sx={{ mr: 1 }}>
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>

            {/* User Menu */}
            <Tooltip title="Account">
              <IconButton
                onClick={handleUserMenuOpen}
                sx={{ p: 0.5 }}
              >
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  {user?.email?.[0]?.toUpperCase() || "A"}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>

        {/* User Menu */}
        <Menu
          anchorEl={anchorElUser}
          open={Boolean(anchorElUser)}
          onClose={handleUserMenuClose}
          TransitionComponent={Fade}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 220,
              bgcolor: 'rgba(15,23,42,0.95)',
              backdropFilter: 'blur(18px)',
              border: '1px solid rgba(148,163,184,0.2)',
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {userProfile?.displayName || user?.email?.split('@')[0]}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <MenuItem onClick={() => { handleUserMenuClose(); handleNavigate('settings'); }}>
            <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Profile Settings</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { handleUserMenuClose(); handleNavigate('settings'); }}>
            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
            <ListItemText>System Settings</ListItemText>
          </MenuItem>
          <Divider sx={{ my: 1, borderColor: 'rgba(148,163,184,0.2)' }} />
          <MenuItem onClick={handleLogout} sx={{ color: 'error.light' }}>
            <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Log Out</ListItemText>
          </MenuItem>
        </Menu>

        {/* Page content area */}
        <Box
          sx={{
            p: { xs: 1, sm: 2, md: 3 },
            mt: { xs: 7, sm: 8 },
            maxWidth: 1400,
            mx: 'auto',
            boxSizing: 'border-box',
          }}
        >
          {/* Breadcrumbs */}
          {renderBreadcrumbs()}

          {/* Page content */}
          {children}
        </Box>
      </Box>
    </Box>
  );
}

export default AdminLayout;